/*
 * 戒导航 · 戒打卡模块（jiedh/checkin）—— 前端「戒瘾目标」模型
 *
 * 设计依据：doc/戒打卡功能设计方案.md §18.3 ②、§18.6、§18.8。
 *
 * ★ 为什么必须注册这个模型（type 必须与后端完全一致）：
 *   后端 JieDH\Checkin\Api\Resource\CheckinHabitResource::type() 返回 'jiedh-checkin-habits'。
 *   里程碑通知的 subject 就是这个模型；Flarum 前端的 app.store **只接收已注册 type**
 *   （common/Store.ts → pushObject 对未注册 type 返回 null 并异步抛错）。
 *   未注册 ⇒ 通知列表拿不到 subject ⇒ 整条通知被静默丢弃（详见 packages/review 的同款事故注释）。
 *   因此必须在前台 extend.ts 里：
 *       new Extend.Store().add('jiedh-checkin-habits', CheckinHabit)
 *   且第一个参数与后端资源 type 完全一致。
 *
 * 属性/关系名与后端 CheckinHabitResource::fields() 一一对应（驼峰，只读的用只读注释标注）。
 * 取值一律用 Model.attribute / Model.hasMany，与核心模型（common/models/User.tsx）风格一致。
 */
import Model from 'flarum/common/Model';
export default class CheckinHabit extends Model {
  /** 目标名称（可写）。 */
  name() {
    return Model.attribute('name').call(this);
  }

  /** 图标：P1 前台直接用 emoji 文本（零依赖零构建，§18.4）。 */
  icon() {
    return Model.attribute('icon').call(this);
  }

  /** 主题色（可空，前端用于卡片点缀，可写）。 */
  color() {
    return Model.attribute('color').call(this);
  }

  /** 计量单位（MVP 固定 'day'）。 */
  unit() {
    return Model.attribute('unit').call(this);
  }

  /** 目标天数（如 100 天计划）。 */
  targetDays() {
    return Model.attribute('targetDays').call(this);
  }

  /** 开始日期（可空）。 */
  startedAt() {
    return Model.attribute('startedAt', v => v ? new Date(v) : null).call(this);
  }

  /** 是否已归档（归档替代物理删除，§18.3）。 */
  isArchived() {
    return Model.attribute('isArchived').call(this);
  }

  /** 是否为默认目标（同一用户唯一，§18.3）。 */
  isDefault() {
    return Model.attribute('isDefault').call(this);
  }

  /** 排序位（只读，后端按 position 稳定排序）。 */
  position() {
    return Model.attribute('position').call(this);
  }

  /** 创建时间（只读）。 */
  createdAt() {
    return Model.attribute('createdAt', v => v ? new Date(v) : null).call(this);
  }

  // ---- 只读聚合统计（§18.6 统计卡，由后端 Service 现算）----

  /** 当前连续天数。 */
  currentStreak() {
    return Model.attribute('currentStreak').call(this);
  }

  /** 最长连续天数。 */
  bestStreak() {
    return Model.attribute('bestStreak').call(this);
  }

  /** 累计成功天数。 */
  totalSuccessDays() {
    return Model.attribute('totalSuccessDays').call(this);
  }

  /** 累计破戒天数（P2 破戒启用后有效）。 */
  totalRelapseDays() {
    return Model.attribute('totalRelapseDays').call(this);
  }

  /** 本月成功天数。 */
  monthSuccessCount() {
    return Model.attribute('monthSuccessCount').call(this);
  }

  /** 成功率（0–1）；无破戒记录时后端返回 null，前端按 100% 展示（§18.6）。 */
  successRate() {
    return Model.attribute('successRate').call(this);
  }

  /**
   * 记录集合关系（includable）。
   * 前端可用 app.store.find('jiedh-checkin-habits', { include: 'records' }) 一次拉取。
   */
  records() {
    return Model.hasMany('records').call(this);
  }
}
flarum.reg.add('jiedh-checkin', 'common/models/CheckinHabit', CheckinHabit);